import type { ReactNode } from 'react';

/**
 * The SideNav's rules, kept out of the component so they can be tested without
 * rendering it: no JSX, no MUI, no router here.
 */

export interface NavItem {
  label: string;
  path?: string;
  icon: ReactNode;
  disabled?: boolean;
  comingSoon?: boolean;
  tooltip?: string;
  /** A live count shown beside the label (#717), read by the SideNav. */
  countKey?: NavCountKey;
}

/**
 * The live counts a nav row can carry: open feedbacks (#717), client imports to
 * handle (#748), Profile Cleaning issues (#753).
 */
export type NavCountKey = 'openFeedbacks' | 'pendingClientImports' | 'cleaningIssues';

/** What a count is of, for the badge's accessible label and tooltip ("3 open"). */
export const NAV_COUNT_WORDING: Record<NavCountKey, string> = {
  openFeedbacks: 'open',
  pendingClientImports: 'client imports to handle',
  cleaningIssues: 'issues',
};

export interface NavSection {
  title?: string;
  items: NavItem[];
}

/**
 * Prefix match on a segment boundary, so `/admin/api` does not light up on
 * `/admin/apifoo`.
 */
const matchesPrefix = (path: string, pathname: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

/**
 * Active detection: exact for client, prefix for admin so detail/edit
 * sub-routes keep their parent lit (`/admin/collection/:id` → "Collection").
 *
 * Most specific wins: when one nav item's path is a prefix of another's, only
 * the deepest match is active: `/admin/profiles/deactivated` lights up
 * "Deactivated Profiles" alone, not "Profiles" as well.
 */
export function isNavPathActive(
  path: string,
  { pathname, navPaths, prefix }: { pathname: string; navPaths: string[]; prefix: boolean },
): boolean {
  if (!prefix) return pathname === path;
  if (!matchesPrefix(path, pathname)) return false;
  return !navPaths.some((other) => other.length > path.length && matchesPrefix(other, pathname));
}

/**
 * What a row's count badge says: nothing at zero or before the count is known
 * (no badge rather than a misleading 0), the number up to 99, then `99+` so the
 * badge keeps its width in a 248px nav.
 */
export function navCountLabel(count: number | null | undefined): string | null {
  if (count === null || count === undefined || count <= 0) return null;
  return count > 99 ? '99+' : String(count);
}

const formatCount = (n: number) => n.toLocaleString('en-US');

/**
 * The lines of the "Profile Cleaning" tooltip (#754): the issues the badge
 * counts, then the "Exits & investments to complete" queue it leaves out, both
 * exact (no `99+` here) with a thousands separator. A figure not read yet has
 * no line; a 0 that was read has one.
 */
export function cleaningCountLines(
  issues: number | null | undefined,
  toComplete: number | null | undefined,
): string[] {
  const lines: string[] = [];
  if (issues !== null && issues !== undefined) {
    lines.push(`${formatCount(issues)} ${issues === 1 ? 'issue' : 'issues'} to clean`);
  }
  if (toComplete !== null && toComplete !== undefined) {
    lines.push(
      `${formatCount(toComplete)} ${toComplete === 1 ? 'exit & investment' : 'exits & investments'} to complete`,
    );
  }
  return lines;
}

/**
 * What the "Profile Cleaning" row's tooltip says, one string per line (#754).
 * Expanded, the row's label is on screen: the tooltip is the figures alone, and
 * there is none when nothing was read or everything is at 0. Collapsed, the
 * tooltip is the row's only text: its label, then the figures read so far.
 */
export function cleaningTooltipLines({
  label,
  issues,
  toComplete,
  collapsed,
}: {
  label: string;
  issues: number | null | undefined;
  toComplete: number | null | undefined;
  collapsed: boolean;
}): string[] {
  const lines = cleaningCountLines(issues, toComplete);
  if (collapsed) return lines.length > 0 ? [`${label}:`, ...lines] : [label];
  const somethingToSay = (issues ?? 0) > 0 || (toComplete ?? 0) > 0;
  return somethingToSay ? lines : [];
}

/**
 * The accessible name of the collapsed "Profile Cleaning" row (#754): its
 * tooltip is two lines of markup, which MUI does not turn into a label, so the
 * row says it itself, like the other collapsed rows ("Collection (9 client
 * imports to handle)"): the label, then the figures read so far.
 */
export function cleaningAccessibleLabel(
  label: string,
  issues: number | null | undefined,
  toComplete: number | null | undefined,
): string {
  const lines = cleaningCountLines(issues, toComplete);
  return lines.length > 0 ? `${label} (${lines.join(', ')})` : label;
}
